import React from 'react'
import { Table, Tag, Typography, Tooltip, Space } from 'antd'
import { WarningOutlined, CheckCircleOutlined, MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { PackingItem } from '../../types'

const { Text } = Typography

interface PackingItemsComparisonProps {
  plannedItems: PackingItem[]
  actualItems: PackingItem[]
  colorDescription?: string
}

interface ComparisonItem {
  key: string
  colorCode: string
  size: string
  barcode: string
  plannedQty: number
  actualQty: number
  status: 'normal' | 'over' | 'under' | 'extra' | 'missing'
  colorDescription?: string
}

const PackingItemsComparison: React.FC<PackingItemsComparisonProps> = ({
  plannedItems,
  actualItems,
  colorDescription
}) => {
  // 对比计划和实际装箱
  const comparisonItems = React.useMemo(() => {
    const itemMap = new Map<string, ComparisonItem>()
    
    // 处理计划装箱 - 合并相同SKU的数量
    plannedItems.forEach(item => {
      const barcode = (item as any).barcode || item.skuBarcode || ''
      // 使用颜色码和尺码作为key，因为barcode可能为空
      const key = `${item.colorCode}-${item.size}`
      const existing = itemMap.get(key)
      
      if (existing) {
        // 合并数量
        existing.plannedQty += item.quantity
      } else {
        itemMap.set(key, {
          key,
          colorCode: item.colorCode,
          size: item.size,
          barcode,
          plannedQty: item.quantity,
          actualQty: 0,
          status: 'missing',
          colorDescription
        })
      }
    })
    
    // 处理实际装箱 - 合并相同SKU的数量
    actualItems.forEach(item => {
      const barcode = (item as any).barcode || item.skuBarcode || ''
      const key = `${item.colorCode}-${item.size}`
      const existing = itemMap.get(key)
      
      if (existing) {
        // 合并数量
        existing.actualQty += item.quantity
        // 更新barcode（如果之前为空）
        if (!existing.barcode && barcode) {
          existing.barcode = barcode
        }
      } else {
        // 清单外
        itemMap.set(key, {
          key,
          colorCode: item.colorCode,
          size: item.size,
          barcode,
          plannedQty: 0,
          actualQty: item.quantity,
          status: 'extra',
          colorDescription
        })
      }
    })
    
    // 更新所有项目的状态
    itemMap.forEach(item => {
      // 如果计划数量为0，说明是清单外录入，保持extra状态
      if (item.plannedQty === 0) {
        item.status = 'extra'
      } else if (item.actualQty === 0 && item.plannedQty > 0) {
        item.status = 'missing'
      } else if (item.actualQty === item.plannedQty) {
        item.status = 'normal'
      } else if (item.actualQty > item.plannedQty) {
        item.status = 'over'
      } else if (item.actualQty < item.plannedQty && item.actualQty > 0) {
        item.status = 'under'
      }
    })
    
    return Array.from(itemMap.values())
  }, [plannedItems, actualItems, colorDescription])

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'normal':
        return {
          color: '#52c41a',
          icon: <CheckCircleOutlined />,
          text: '正常',
          bgColor: '#f6ffed'
        }
      case 'over':
        return {
          color: '#FF8C00',
          icon: <PlusCircleOutlined />,
          text: '超件',
          bgColor: '#fff7e6'
        }
      case 'under':
        return {
          color: '#DC143C',
          icon: <MinusCircleOutlined />,
          text: '缺件',
          bgColor: '#fff1f0'
        }
      case 'extra':
        return {
          color: '#1890ff',
          icon: <WarningOutlined />,
          text: '清单外',
          bgColor: '#e6f7ff'
        }
      case 'missing':
        return {
          color: '#d9d9d9',
          icon: <MinusCircleOutlined />,
          text: '未装',
          bgColor: '#fafafa'
        }
      default:
        return {
          color: '#000',
          icon: null,
          text: '',
          bgColor: '#fff'
        }
    }
  }

  const columns: ColumnsType<ComparisonItem> = [
    {
      title: '颜色码',
      dataIndex: 'colorCode',
      key: 'colorCode',
      width: 90,
      render: (colorCode: string) => (
        <Tag color="blue">{colorCode}</Tag>
      )
    },
    {
      title: '颜色',
      dataIndex: 'colorDescription',
      key: 'colorDescription',
      width: 120,
      render: (description: string) => (
        <Text>{description || '-'}</Text>
      )
    },
    {
      title: '尺码',
      dataIndex: 'size',
      key: 'size',
      width: 70,
      align: 'center',
      render: (size: string) => (
        <Tag color="green">{size}</Tag>
      )
    },
    {
      title: '条码',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 130,
      render: (barcode: string) => {
        if (!barcode) {
          return <Text style={{ color: '#999' }}>-</Text>
        }
        return (
          <Text 
            code 
            copyable={{ text: barcode }}
            style={{ fontSize: '13px', fontFamily: 'monospace' }}
          >
            {barcode}
          </Text>
        )
      }
    },
    {
      title: '计划数量',
      dataIndex: 'plannedQty',
      key: 'plannedQty',
      width: 90,
      align: 'center',
      render: (qty: number) => (
        <Text>{qty}</Text>
      )
    },
    {
      title: '实际数量',
      dataIndex: 'actualQty',
      key: 'actualQty',
      width: 90,
      align: 'center',
      render: (qty: number, record: ComparisonItem) => {
        const config = getStatusConfig(record.status)
        return (
          <Text strong style={{ color: config.color }}>
            {qty}
          </Text>
        )
      }
    },
    {
      title: '差异',
      key: 'diff',
      width: 80,
      align: 'center',
      render: (_, record: ComparisonItem) => {
        const diff = record.actualQty - record.plannedQty
        if (diff === 0) return <Text>-</Text>
        const config = getStatusConfig(record.status)
        return (
          <Text strong style={{ color: config.color }}>
            {diff > 0 ? `+${diff}` : diff}
          </Text>
        )
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: string) => {
        const config = getStatusConfig(status)
        return (
          <Tooltip title={config.text}>
            <Tag color={config.color} icon={config.icon}>
              {config.text}
            </Tag>
          </Tooltip>
        )
      }
    }
  ]

  const totalPlanned = comparisonItems.reduce((sum, item) => sum + item.plannedQty, 0)
  const totalActual = comparisonItems.reduce((sum, item) => sum + item.actualQty, 0)

  return (
    <div style={{ marginTop: 4, marginBottom: 8 }}>
      <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Space size="large">
          <Text type="secondary">计划: <Text strong>{totalPlanned}</Text> 件</Text>
          <Text type="secondary">实际: <Text strong>{totalActual}</Text> 件</Text>
          <Text style={{ color: totalActual - totalPlanned >= 0 ? '#ff4d4f' : '#DC143C' }}>
            差异: <Text strong>{totalActual - totalPlanned >= 0 ? `+${totalActual - totalPlanned}` : totalActual - totalPlanned}</Text> 件
          </Text>
        </Space>
      </div>
      
      <Table
        columns={columns}
        dataSource={comparisonItems}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 750 }}
        rowClassName={() => ''}
        onRow={(record) => {
          const config = getStatusConfig(record.status)
          return {
            style: {
              backgroundColor: config.bgColor
            }
          }
        }}
      />
    </div>
  )
}

export default PackingItemsComparison
